
import React from 'react';

const NoiseMonitoringSection: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Noise Monitoring
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                Noise monitoring involves assessing the sound levels within a workplace to prevent hearing loss and other auditory issues among workers. This type of exposure not only causes immediate discomfort but can also lead to serious long-term health issues such as permanent hearing loss, stress, and heart disease.
              </p>
              <p className="text-gray-700 mb-8 leading-relaxed">
                Recommendations for hearing protection, annual audiograms, and adjustments to the workplace environment are part of the corrective actions following noise monitoring.
              </p>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
                <h4 className="font-semibold text-red-800 mb-2">Health Risks of Noise Exposure:</h4>
                <ul className="text-red-700 space-y-1">
                  <li>• Permanent hearing loss</li>
                  <li>• Increased stress levels</li>
                  <li>• Cardiovascular disease</li>
                  <li>• Reduced productivity</li>
                  <li>• Communication difficulties</li>
                </ul>
              </div>

              <a 
                href="#contact-form" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Schedule Noise Assessment
              </a>
            </div>

            <div>
              <img 
                src="https://static.readdy.ai/image/83c31d00f47a619d4f0a515f0b3bc954/5dce56af6078fa377e52f58991db2089.jpeg"
                alt="Industrial Hygiene Noise Monitoring"
                className="w-full h-[400px] object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>

          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Our Noise Monitoring Process
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-volume-up-line text-blue-600 text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-3">Initial Assessment</h4>
                <p className="text-gray-700">
                  Quick evaluation using uncalibrated equipment to identify potential noise hazards and high-risk areas.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-equalizer-line text-green-600 text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-3">Calibrated Monitoring</h4>
                <p className="text-gray-700">
                  Precise measurement using calibrated noise dosimeters during full work shifts to determine actual exposure levels.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-purple-600 text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-3">Control Implementation</h4>
                <p className="text-gray-700">
                  Development of engineering controls, administrative measures, and PPE recommendations to reduce exposure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NoiseMonitoringSection;
